import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useMemo, useState } from "react";
import {
  shouldBlockDismiss,
  startErrorText,
  useCancelRelink,
  useRelinkSession,
  useStartRelink,
  useSubmitInput,
} from "./hooks";
import { CodeStep } from "./steps/code-step";
import { DoneStep } from "./steps/done-step";
import { ExpiredStep } from "./steps/expired-step";
import { FailedStep } from "./steps/failed-step";
import { IntroStep } from "./steps/intro-step";
import { PendingStep } from "./steps/pending-step";
import { QrStep } from "./steps/qr-step";
import { TwoFaStep } from "./steps/twofa-step";
import { VerifyingStep } from "./steps/verifying-step";

export function RelinkWizard({
  avatarId,
  avatarName,
  avatarPlatform,
  avatarStatus,
  expectedGroups,
  onClose,
}: {
  avatarId: number;
  avatarName: string;
  avatarPlatform: string;
  avatarStatus: string;
  expectedGroups: number;
  onClose: () => void;
}) {
  const startMut = useStartRelink();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const { data: session } = useRelinkSession(sessionId);

  const cancelMut = useCancelRelink(sessionId ?? "");
  const inputMut = useSubmitInput(sessionId ?? "");

  const handleStart = () => {
    startMut.mutate(avatarId, {
      onSuccess: (s) => setSessionId(s.session_id),
    });
  };

  const handleCancel = () => {
    if (sessionId) {
      cancelMut.mutate(undefined, { onSettled: onClose });
    } else {
      onClose();
    }
  };

  const handleRetry = () => {
    setSessionId(null);
    startMut.reset();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: handleCancel/handleRetry/handleStart are recreated each render; closures are stable.
  const step = useMemo(() => {
    // Intro pane: shown until the operator clicks Start Relink. No mutation
    // fires until they confirm. Eliminates the "I clicked, nothing happened"
    // surprise from auto-start + vague spinner.
    if (sessionId === null) {
      return (
        <IntroStep
          avatarName={avatarName}
          platform={avatarPlatform}
          status={avatarStatus}
          expectedGroups={expectedGroups}
          isPending={startMut.isPending}
          error={startErrorText(startMut.error)}
          onStart={handleStart}
          onCancel={onClose}
        />
      );
    }
    if (!session) {
      // Session created on backend but first /relink/<id> poll hasn't returned yet.
      return (
        <PendingStep avatarName={avatarName} platform={avatarPlatform} onCancel={handleCancel} />
      );
    }
    switch (session.state) {
      case "pending":
        return (
          <PendingStep
            avatarName={session.avatar_name}
            platform={session.platform}
            onCancel={handleCancel}
          />
        );
      case "qr_ready":
        return (
          <QrStep
            qrText={session.payload_json.qr_text}
            pairingCode={session.payload_json.pairing_code}
            instruction={session.payload_json.instruction}
            platform={session.platform}
            onCancel={handleCancel}
          />
        );
      case "awaiting_code":
        return (
          <CodeStep
            prompt={session.payload_json.prompt ?? ""}
            onSubmit={(v) => inputMut.mutate(v)}
            onCancel={handleCancel}
            isPending={inputMut.isPending}
          />
        );
      case "awaiting_2fa":
        return (
          <TwoFaStep
            prompt={session.payload_json.prompt ?? ""}
            onSubmit={(v) => inputMut.mutate(v)}
            onCancel={handleCancel}
            isPending={inputMut.isPending}
          />
        );
      case "verifying":
        return <VerifyingStep />;
      case "done":
        return (
          <DoneStep
            groupsTotal={session.result_json.groups_total ?? null}
            expectedGroups={expectedGroups}
            onClose={onClose}
          />
        );
      case "failed":
        return <FailedStep error={session.error} onRetry={handleRetry} onClose={onClose} />;
      case "expired":
        return <ExpiredStep onRetry={handleRetry} onClose={onClose} />;
      case "cancelled":
        setTimeout(onClose, 1500);
        return <div style={{ padding: 24, textAlign: "center" }}>Cancelled.</div>;
      default:
        return null;
    }
  }, [
    session,
    sessionId,
    expectedGroups,
    inputMut,
    onClose,
    startMut.isPending,
    avatarName,
    avatarPlatform,
    avatarStatus,
  ]);

  const title = session
    ? `Relinking ${session.avatar_name} (${session.platform})`
    : `Relink ${avatarName} (${avatarPlatform})`;

  // While a relink is in flight, a stray backdrop click or Escape must NOT
  // close the wizard — that orphans the session on the backend and loses the
  // pairing code (the S3_1P incident, IR-1199). Operators close via the
  // explicit Cancel button instead.
  const blockDismiss = shouldBlockDismiss(sessionId, session?.state);

  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v && !blockDismiss) onClose();
      }}
    >
      <DialogContent
        className="max-w-[480px]"
        onInteractOutside={(e) => {
          if (blockDismiss) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (blockDismiss) e.preventDefault();
        }}
      >
        <DialogTitle>{title}</DialogTitle>
        {step}
      </DialogContent>
    </Dialog>
  );
}
